import { ChoiceButtonInput, ChoiceButtonOption } from '@app/app/_components/Input/ChoiceButtonInput';
import { Tag } from '@app/app/_components/Tag/Tag';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { splitNumber } from '@app/lib/split-number/splitNumber';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useLocale, useTranslations } from 'next-intl';
import { ChangeEvent, useMemo } from 'react';
import { Message } from '@app/app/_components/Message/Message';
import { PersonalInsuranceRiskPackage } from '@app/domain/model/eshop/personal-accidents/PersonalInsuranceRiskPackage';
import { IncludedCovers } from '@app/app/esales/personal-accidents/_sections/steps/InsuranceRisksStep/IncludedCovers';
import { Button } from '@app/app/_components/Button/Button';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    risks: PersonalInsuranceRiskPackage[];
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    defaultChecked?: string;
}

export function InsuranceRisksStep({ risks, onChange, value, defaultChecked }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const locale = useLocale();
    const parameters = useAppParameters();

    const options: ChoiceButtonOption[] = useMemo(() => {
        return risks.map(risk => {
            const hasDiscount =
                risk.premiumAfterDiscount != null &&
                risk.premium != null &&
                risk.premiumAfterDiscount > 0 &&
                risk.premiumAfterDiscount < risk.premium;

            const displayPrice = hasDiscount ? risk.premiumAfterDiscount! : (risk.premium ?? 0);
            const splittedPrice = splitNumber(displayPrice);

            let discountPercentage = 0;
            if (hasDiscount && risk.premium) {
                discountPercentage = ((risk.premium - risk.premiumAfterDiscount!) / risk.premium) * 100;
            }

            return {
                value: risk.code ?? '',
                label: (
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
                        <div className="flex flex-1 flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-3">
                                <Heading level="h4" className="text-xl">
                                    {risk.name}
                                </Heading>
                                {hasDiscount && (
                                    <Tag variant="promo">
                                        {t('eshop.transport.save')} {discountPercentage.toFixed(0)}%
                                    </Tag>
                                )}
                            </div>

                            <div className="flex flex-col gap-1">
                                {hasDiscount && risk.premium && (
                                    <Paragraph isGray size="tiny" className="line-through">
                                        {formatEuroValue(risk.premium, tenant)}
                                    </Paragraph>
                                )}
                                <div className="flex items-baseline gap-1">
                                    <Paragraph isBold size="medium">
                                        {splittedPrice.integerPart}
                                    </Paragraph>
                                    <Paragraph isBold size="tiny" className="self-start">
                                        {splittedPrice.fractionalPart}
                                    </Paragraph>
                                    <Paragraph isBold size="medium">
                                        €
                                    </Paragraph>
                                    <Paragraph isGray className="ml-1">
                                        {`/ ${t.rich('eshop.personalAccidents.months', {
                                            count: 12,
                                        })}`}
                                    </Paragraph>
                                </div>
                            </div>
                        </div>
                    </div>
                ),
                details: (
                    <div className="flex flex-col gap-4">
                        <IncludedCovers covers={risk.includedCovers} />
                    </div>
                ),
            };
        });
    }, [risks, t, tenant]);

    return (
        <div className="flex flex-col gap-6">
            <ChoiceButtonInput
                name="personalInsuranceRisk"
                options={options}
                onChange={onChange}
                value={value}
                defaultChecked={defaultChecked}
            />
            <Message
                className="md:flex-row md:items-start"
                variant="info"
                addon={
                    <Button
                        preventDefault
                        variant="outlined"
                        href={
                            locale === 'lt'
                                ? parameters.eshopPersonalAccidentsInfoUrls.compareLt
                                : parameters.eshopPersonalAccidentsInfoUrls.compareEn
                        }
                        size="tiny"
                        target="_blank"
                        rightAddon={<ArrowRightIcon className="size-4" />}>
                        {t('eshop.general.compare')}
                    </Button>
                }>
                <div className="flex flex-col gap-2">
                    <Paragraph isBold>{t('eshop.general.compareInsurances')}</Paragraph>
                    <Paragraph size="small">{t('eshop.personalAccidents.compareInsurancesMessage')}</Paragraph>
                </div>
            </Message>
        </div>
    );
}
